// <stacked-bar-chart> — the statement page's revenue-by-week chart: one
// stack per week, series bottom-to-top in payload order (non-digital under
// digital, per the mockups). Light-DOM enhancement of server-rendered
// markup: the component renders the JSON payload and a fixed-height canvas
// wrapper; this element only draws.
//
//   <stacked-bar-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative h-56"><canvas></canvas></div>
//   </stacked-bar-chart>
//
// The series legend is server-rendered HTML in the card header, not
// Chart.js's — so the Legend plugin never ships.

import { Chart } from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	money,
	readPayload,
} from "./chart-theme";

type Payload = {
	currency: string;
	// Stack order, bottom first.
	series: { label: string; color: string }[];
	// title is the tooltip heading ("Week of Oct 20, 2025" / "October 2025");
	// the axis shows the shorter label.
	points: { label: string; title?: string; values: number[] }[];
};

class StackedBarChart extends HTMLElement {
	#chart: Chart<"bar"> | null = null;

	connectedCallback() {
		ensureChartTheme();
		const payload = readPayload<Payload>(this, "stacked-bar-chart");
		const topIndex = payload.series.length - 1;

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "stacked-bar-chart"), {
			type: "bar",
			data: {
				labels: payload.points.map((point) => point.label),
				datasets: payload.series.map((series, index) => ({
					label: series.label,
					data: payload.points.map((point) => point.values[index] ?? 0),
					backgroundColor: chartColor(series.color),
					stack: "revenue",
					// Round only the top of the stack; lower segments stay square.
					borderRadius:
						index === topIndex
							? { topLeft: 3, topRight: 3, bottomLeft: 0, bottomRight: 0 }
							: 0,
					maxBarThickness: 48,
					categoryPercentage: 0.78,
					barPercentage: 0.94,
				})),
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				animation: { duration: 300 },
				interaction: { mode: "index", intersect: false },
				plugins: {
					tooltip: {
						// Point-level title, per-series rows, exact total footer.
						callbacks: {
							title: (items) => {
								const first = items[0];
								if (!first) return "";
								return payload.points[first.dataIndex]?.title ?? first.label;
							},
							label: (item) =>
								` ${item.dataset.label}: ${money(item.parsed.y ?? 0, payload.currency)}`,
							footer: (items) => {
								const total = items.reduce(
									(sum, item) => sum + (item.parsed.y ?? 0),
									0,
								);
								return `Total: ${money(total, payload.currency)}`;
							},
						},
					},
				},
				scales: {
					x: {
						stacked: true,
						grid: { display: false },
						border: { display: false },
						ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
					},
					y: {
						stacked: true,
						// Hard floor at zero: royalty accrual shouldn't rescale around
						// a stray net-negative bucket (an unflagged reversal alone in a
						// sparse period would otherwise drag the axis a whole tick step
						// below zero). A negative period renders as an empty slot; its
						// real figures still show in the index-mode tooltip.
						min: 0,
						border: { display: false },
						grid: { color: "rgba(255, 255, 255, 0.06)" },
						ticks: {
							maxTicksLimit: 5,
							callback: (value) =>
								money(Number(value), payload.currency, "compact"),
						},
					},
				},
			},
		});
	}

	disconnectedCallback() {
		this.#chart?.destroy();
		this.#chart = null;
	}
}

if (!customElements.get("stacked-bar-chart")) {
	customElements.define("stacked-bar-chart", StackedBarChart);
}
